import React, { useState, useEffect } from 'react';
import { governanceAPI } from '../../../lib/governanceApi';
import { toast } from 'sonner';
import { Loader2, SlidersHorizontal, ShieldAlert, Sparkles } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Checkbox } from '../../../components/ui/checkbox';
import { Skeleton } from '../../../components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '../../../components/ui/alert';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';

const PLANS = ['free', 'trial', 'growth', 'scale', 'enterprise'];

const Field = ({ label, children, testId }) => (
  <div className="space-y-1" data-testid={testId}>
    <Label className="gov-text-secondary text-xs">{label}</Label>
    {children}
  </div>
);

export const ManualOverrideDialog = ({ open, onOpenChange, tenantId, onApplied }) => {
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState(null);
  const [inv, setInv] = useState({});
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!open || !tenantId) return;
    (async () => {
      setLoading(true);
      setInfo(null);
      setForm(null);
      try {
        const res = await governanceAPI.getBillingOverride(tenantId);
        const d = res.data;
        setInfo(d);
        const s = d.suggestion || {};
        const c = d.current || {};
        const b = d.billing || {};
        setForm({
          tenant_type: s.tenant_type || c.tenant_type || 'TRIAL',
          subscription_plan: s.subscription_plan || c.subscription_plan || 'free',
          is_demo: s.is_demo ?? c.is_demo ?? true,
          execution_mode: s.execution_mode || c.execution_mode || 'SIMULATED',
          billing_status: s.billing_status || b.billing_status || 'cancelled',
          paid_through: String(s.paid_through || b.paid_through || '').slice(0, 10),
          current_cycle_end: String(s.current_cycle_end || b.current_cycle_end || '').slice(0, 10),
          razorpay_subscription_id: s.razorpay_subscription_id || b.razorpay_subscription_id || '',
          active_subscription_id: s.active_subscription_id || b.active_subscription_id || '',
          note: '',
        });
        const sel = {};
        (d.invoices || []).forEach((i) => { sel[i.invoice_number] = { checked: false, payment_id: '' }; });
        (s.unvoid_invoices || []).forEach((u) => {
          sel[u.invoice_number] = { checked: true, payment_id: u.payment_id || '' };
        });
        setInv(sel);
      } catch (e) {
        toast.error('Failed to load override data', { description: e?.response?.data?.detail || e.message });
        onOpenChange(false);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, tenantId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const apply = async () => {
    setApplying(true);
    try {
      const payload = {
        tenant_type: form.tenant_type,
        subscription_plan: form.subscription_plan,
        is_demo: !!form.is_demo,
        execution_mode: form.execution_mode,
        billing_status: form.billing_status,
        paid_through: form.paid_through ? `${form.paid_through}T23:59:59+00:00` : null,
        current_cycle_end: form.current_cycle_end ? `${form.current_cycle_end}T23:59:59+00:00` : null,
        razorpay_subscription_id: form.razorpay_subscription_id || null,
        active_subscription_id: form.active_subscription_id || null,
        note: form.note || null,
        unvoid_invoices: Object.entries(inv)
          .filter(([, v]) => v.checked)
          .map(([n, v]) => ({ invoice_number: n, payment_id: v.payment_id || null })),
      };
      const res = await governanceAPI.applyBillingOverride(tenantId, payload);
      const d = res.data || {};
      toast.success('Manual override applied', {
        description: `${d.transaction ? 'Atomic transaction' : 'Sequential write'}${
          d.unvoided_invoices?.length ? ` · un-voided ${d.unvoided_invoices.join(', ')}` : ''}`,
      });
      onOpenChange(false);
      onApplied?.();
    } catch (e) {
      toast.error('Override failed', { description: e?.response?.data?.detail || e.message });
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="manual-override-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <SlidersHorizontal className="h-5 w-5" /> Manual Plan Override
            {info?.tenant_name ? ` — ${info.tenant_name}` : ''}
          </DialogTitle>
          <DialogDescription>
            Superadmin-only. Writes directly to <code>tenants</code> + <code>tenant_billing</code>{' '}
            (atomic) — no live subscription required. Fully audit-logged.
          </DialogDescription>
        </DialogHeader>

        {loading || !form ? (
          <div className="space-y-3 py-4">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
        ) : (
          <div className="space-y-4">
            {info?.suggestion?.reason && (
              <Alert className="border-sky-500/40 bg-sky-500/10" data-testid="override-suggestion">
                <Sparkles className="h-4 w-4 text-sky-400" />
                <AlertTitle>Suggested fix (pre-filled from live Razorpay)</AlertTitle>
                <AlertDescription>{info.suggestion.reason}</AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-2 gap-3">
              <Field label="Tenant type" testId="override-tenant-type">
                <Select value={form.tenant_type} onValueChange={(v) => set('tenant_type', v)}>
                  <SelectTrigger data-testid="override-tenant-type-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PAID">PAID</SelectItem>
                    <SelectItem value="TRIAL">TRIAL</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Subscription plan" testId="override-plan">
                <Select value={form.subscription_plan} onValueChange={(v) => set('subscription_plan', v)}>
                  <SelectTrigger data-testid="override-plan-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PLANS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Billing status" testId="override-billing-status">
                <Select value={form.billing_status} onValueChange={(v) => set('billing_status', v)}>
                  <SelectTrigger data-testid="override-billing-status-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">active</SelectItem>
                    <SelectItem value="cancelled">cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Execution mode" testId="override-exec-mode">
                <Select value={form.execution_mode} onValueChange={(v) => set('execution_mode', v)}>
                  <SelectTrigger data-testid="override-exec-mode-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LIVE">LIVE</SelectItem>
                    <SelectItem value="SIMULATED">SIMULATED</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Paid through" testId="override-paid-through">
                <Input type="date" value={form.paid_through}
                  onChange={(e) => set('paid_through', e.target.value)} data-testid="override-paid-through-input" />
              </Field>
              <Field label="Current cycle end" testId="override-cycle-end">
                <Input type="date" value={form.current_cycle_end}
                  onChange={(e) => set('current_cycle_end', e.target.value)} data-testid="override-cycle-end-input" />
              </Field>
              <Field label="Razorpay subscription id" testId="override-rzp-sub">
                <Input value={form.razorpay_subscription_id} placeholder="sub_…"
                  onChange={(e) => set('razorpay_subscription_id', e.target.value)}
                  data-testid="override-rzp-sub-input" />
              </Field>
              <Field label="Active subscription id" testId="override-active-sub">
                <Input value={form.active_subscription_id} placeholder="sub_…"
                  onChange={(e) => set('active_subscription_id', e.target.value)}
                  data-testid="override-active-sub-input" />
              </Field>
            </div>

            <div className="flex items-center justify-between rounded-md border border-[var(--gov-border)] px-3 py-2">
              <div>
                <div className="gov-text-primary text-sm">Demo mode</div>
                <div className="gov-text-tertiary text-xs">OFF = real customer workspace (clears demo banners)</div>
              </div>
              <Switch checked={!!form.is_demo} onCheckedChange={(v) => set('is_demo', v)}
                data-testid="override-is-demo-switch" />
            </div>

            {(info?.invoices || []).length > 0 && (
              <div className="space-y-2">
                <Label className="gov-text-secondary text-xs">Un-void invoices (restore to paid — tick + optional real payment id)</Label>
                <div className="rounded-md border border-[var(--gov-border)] divide-y divide-[var(--gov-border)]">
                  {info.invoices.map((i) => {
                    const row = inv[i.invoice_number] || { checked: false, payment_id: '' };
                    const isVoid = i.status === 'void';
                    return (
                      <div key={i.invoice_number} className="flex items-center gap-2 px-3 py-2 text-sm">
                        <Checkbox checked={row.checked} disabled={!isVoid}
                          onCheckedChange={(v) => setInv((m) => ({ ...m, [i.invoice_number]: { ...row, checked: !!v } }))}
                          data-testid={`override-unvoid-${i.invoice_number}`} />
                        <div className="flex-1 min-w-0">
                          <span className="gov-text-primary">{i.invoice_number}</span>
                          <span className={`ml-2 rounded px-1.5 py-0.5 text-[10px] ${isVoid ? 'bg-red-500/15 text-red-400' : 'bg-emerald-500/15 text-emerald-500'}`}>
                            {i.status}
                          </span>
                          <span className="gov-text-tertiary ml-2 text-xs">
                            ₹{i.total_amount} · {String(i.invoice_date || '').slice(0, 10)}
                          </span>
                        </div>
                        {isVoid && row.checked && (
                          <Input className="h-7 w-44 text-xs" placeholder="pay_… (real payment id)"
                            value={row.payment_id}
                            onChange={(e) => setInv((m) => ({ ...m, [i.invoice_number]: { ...row, payment_id: e.target.value } }))}
                            data-testid={`override-unvoid-pid-${i.invoice_number}`} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <Field label="Audit note (why this override?)" testId="override-note">
              <Input value={form.note} placeholder="e.g. paid customer wrongly downgraded on cancel — restore paid-through access"
                onChange={(e) => set('note', e.target.value)} data-testid="override-note-input" />
            </Field>

            <Alert className="border-amber-500/40 bg-amber-500/10">
              <ShieldAlert className="h-4 w-4 text-amber-500" />
              <AlertDescription className="text-xs">
                This bypasses all billing validation. The action is recorded in the superadmin audit
                trail with before/after snapshots.
              </AlertDescription>
            </Alert>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="destructive" onClick={apply} disabled={applying || loading || !form}
            data-testid="override-apply-btn">
            {applying ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <SlidersHorizontal className="mr-1 h-4 w-4" />}
            Apply override
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
